import React, { useCallback } from "react";
import { useStyles } from "./CardFormStyles";
import { usePaymentInputs } from "react-payment-inputs";
import { FieldProps, Field as FormField } from "formik";
import { CardFormValues } from "../../../common";
import { Field, Input } from "@fluentui/react-components";
import { images } from "./CardImages";
export const CardForm = () => {
  const styles = useStyles();
  const {
    meta: { touchedInputs, erroredInputs },
    getCardImageProps,
    getCardNumberProps,
    getExpiryDateProps,
    getCVCProps,
  } = usePaymentInputs();
  const cardImageProps = getCardImageProps({ images });
  const { children, ...imgProps } = cardImageProps as {
    children?: React.ReactNode;
  };
  const cardImage = children ? (
    <svg className={styles.image}>{children as React.ReactNode}</svg>
  ) : (
    <svg className={styles.image} {...imgProps} />
  );
  const [cardNumber, setCardNumber] = React.useState<string>("");
  const [cvc, setCvc] = React.useState<string>("");

  const maskSensitiveInfo = (inputValue: string): string => {
    const digits = inputValue.replace(/\D/g, "");
    const masked = digits.replace(/\d/g, "*");
    return masked.replace(/(.{4})(?=.)/g, "$1");
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const inputValue = e.target.value;
    setCardNumber(inputValue);
  };

  const handleCardNumberBlur = (): void => {
    setCardNumber(maskSensitiveInfo(cardNumber));
  };

  const handleCardNumberFocus = (): void => {
    setCardNumber(cardNumber.replace(/\D/g, ""));
  };

  const handleCvcNumberChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const inputValue = e.target.value;
    setCvc(inputValue);
  };

  const handlevCvcBlur = (): void => {
    setCvc(maskSensitiveInfo(cvc)); // Mask the existing value
  };

  const handleCvcFocus = (): void => {
    setCvc(cvc); // Mask the existing value
  };

  const validateCardholderName = (value: string) => {
    if (value === "") {
      return "Enter a cardholder name";
    }
    return undefined;
  };
  const validateCardNumber = useCallback(() => {
    if (touchedInputs.cardNumber && erroredInputs.cardNumber) {
      return erroredInputs.cardNumber;
    }
    return undefined;
  }, [touchedInputs, erroredInputs]);
  const validateExpirationDate = useCallback(() => {
    if (touchedInputs.expiryDate && erroredInputs.expiryDate) {
      return erroredInputs.expiryDate;
    }
    return undefined;
  }, [touchedInputs, erroredInputs]);
  const validateSecurityCode = useCallback(() => {
    if (touchedInputs.cvc && erroredInputs.cvc) {
      return erroredInputs.cvc;
    }
    return undefined;
  }, [touchedInputs, erroredInputs]);

  const cvcInputRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (!cvc && cvcInputRef.current) {
      setCvc(maskSensitiveInfo(cvcInputRef.current.value)); // Set the state to the current value of the input field
    }
  }, [cvc]);
  return (
    <div className={styles.container} data-testid="CardForm">
      {/* Cardholder Name Field */}
      <FormField name="cardholderName" validate={validateCardholderName}>
        {({ field, meta }: FieldProps<CardFormValues["cardholderName"]>) => (
          <Field
            className={styles.field}
            label={"Cardholder Name"}
            required
            validationMessage={meta.touched && meta.error ? meta.error : undefined}
          >
            <Input
              {...field}
              appearance="underline"
              placeholder="John Doe"
              type="text"
              value={field.value}
              data-testid="cardholderName"
              tabIndex={2}
            />
          </Field>
        )}
      </FormField>

      {/* Card Number Field */}
      <FormField name="cardNumber" validate={validateCardNumber}>
        {({ field, meta }: FieldProps<CardFormValues["cardNumber"]>) => (
          <Field
            className={styles.field}
            label={"Card Number"}
            required
            validationMessage={meta.touched && meta.error ? meta.error : undefined}
          >
            <Input
              {...field}
              {...getCardNumberProps({
                onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                  field.onBlur(e);
                  handleCardNumberBlur();
                },
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  field.onChange(e);
                  handleCardNumberChange(e);
                },
                onFocus: () => {
                  handleCardNumberFocus();
                },
              })}
              appearance="underline"
              contentBefore={cardImage}
              placeholder="0000 0000 0000 0000"
              type="text"
              data-testid="cardNumber"
              tabIndex={3}
              value={cardNumber ? cardNumber : field.value}
            />
          </Field>
        )}
      </FormField>

      {/* Expiration Date Field */}
      <FormField name="expiryDate" validate={validateExpirationDate}>
        {({ field, meta }: FieldProps<CardFormValues["expiryDate"]>) => (
          <Field
            className={styles.field}
            label={"Expiration Date"}
            required
            validationMessage={meta.touched && meta.error ? meta.error : undefined}
          >
            <Input
              {...field}
              {...getExpiryDateProps({ onChange: field.onChange, onBlur: field.onBlur })}
              appearance="underline"
              placeholder="MM/YY"
              type="text"
              data-testid="cardExpirationDate"
              value={field.value}
              tabIndex={4}
            />
          </Field>
        )}
      </FormField>

      {/* Security Code Field */}
      <FormField name="cvc" validate={validateSecurityCode}>
        {({ field, meta }: FieldProps<CardFormValues["cvc"]>) => (
          <Field
            className={styles.field}
            label={"CVC"}
            required
            validationMessage={meta.touched && meta.error ? meta.error : undefined}
          >
            <Input
              {...field}
              {...getCVCProps({
                onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                  field.onBlur(e);
                  handlevCvcBlur();
                },
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  field.onChange(e);
                  handleCvcNumberChange(e);
                },
                onFocus: () => {
                  handleCvcFocus();
                },
              })}
              ref={cvcInputRef}
              appearance="underline"
              type="text"
              data-testid="cardCvc"
              value={cvc ? cvc : field.value}
              tabIndex={5}
            />
          </Field>
        )}
      </FormField>
    </div>
  );
};
